import { Check, ChevronDownIcon, Clock } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { DayPicker } from "@/components/day-picker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SELECT_TRIGGER_CLASS } from "@/components/ui/select";
import { shiftDay } from "@/lib/calendar";
import { belgianDay, formatDate, formatDateTime, formatDayMonth, formatTime } from "@/lib/format";
import { periodKey, PERIODS } from "@/lib/period";
import {
  customRange,
  momentParts,
  parseTime,
  type RangeProblem,
  type TimeRange,
} from "@/lib/time-range";
import { cn } from "@/lib/utils";

interface TimeRangeFilterProps {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
  className?: string;
}

/** One end of the custom range as the boxes hold it. */
interface EndDraft {
  day: string | null;
  time: string;
}

/** The boxes' first values: the range on show, or the current period's days from 00:00 to 23:59. */
function draftOf(value: TimeRange, now: Date): { from: EndDraft; to: EndDraft } {
  if (value.kind === "custom") {
    const from = momentParts(value.from);
    const to = momentParts(value.to);
    if (from && to) {
      return { from, to };
    }
  }
  const today = belgianDay(now) ?? "";
  const days = value.kind === "period" ? value.days : PERIODS[0];
  return {
    from: { day: today ? shiftDay(today, -(days - 1)) : null, time: "00:00" },
    to: { day: today || null, time: "23:59" },
  };
}

/**
 * A moment in short for the button: "12/10 08:00" in this year,
 * "12/10/2025 08:00" in another.
 */
function shortMoment(moment: string, now: Date): string {
  const day = belgianDay(moment) ?? "";
  const sameYear = day.slice(0, 4) === (belgianDay(now) ?? "").slice(0, 4);
  return `${sameYear ? formatDayMonth(day) : formatDate(moment)} ${formatTime(moment)}`;
}

/**
 * The time range of a table with times, in its filter bar: Afgelopen week,
 * Afgelopen maand, Afgelopen kwartaal, or "Aangepast", which opens a from and
 * a to, each a day (`<DayPicker>`) and a time (HH:MM), in Belgian time. The
 * end includes its whole minute. "Toepassen" checks that the end comes after
 * the start and that the range is at most a year, and says so otherwise. The
 * button names the range ("12/10 08:00 tot 14/10 17:00"). Keep the range in
 * the URL with `useTimeRange` (`@/lib/time-range`), and pass it to the query
 * with `timeRangeQuery`:
 *
 *     const [range, setRange] = useTimeRange();
 *     <FilterBar columns={["wide", "medium"]} …>
 *       <TimeRangeFilter value={range} onChange={setRange} />
 *       …
 *     </FilterBar>
 */
export function TimeRangeFilter({ value, onChange, className }: TimeRangeFilterProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [draft, setDraft] = useState(() => draftOf(value, new Date()));
  const [problem, setProblem] = useState<RangeProblem | null>(null);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (next) {
      setCustom(value.kind === "custom");
      setDraft(draftOf(value, new Date()));
      setProblem(null);
    }
  };

  const pickPeriod = (days: (typeof PERIODS)[number]) => {
    onChange({ kind: "period", days });
    setOpen(false);
  };

  const apply = () => {
    const result = customRange(draft.from, draft.to);
    if ("problem" in result) {
      setProblem(result.problem);
      return;
    }
    onChange(result.range);
    setOpen(false);
  };

  const now = new Date();
  const label =
    value.kind === "custom"
      ? t("timeRange.range", {
          from: shortMoment(value.from, now),
          to: shortMoment(value.to, now),
        })
      : t(`period.last.${periodKey(value.days)}`);
  const fullLabel =
    value.kind === "custom"
      ? t("timeRange.range", {
          from: formatDateTime(value.from),
          to: formatDateTime(value.to),
        })
      : label;

  const option = (selected: boolean, text: string, onSelect: () => void, key: string) => (
    <li key={key}>
      <button
        type="button"
        aria-pressed={selected}
        onClick={onSelect}
        className={cn(
          "focus-visible:ring-ring flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm focus-visible:ring-2 focus-visible:outline-none pointer-coarse:py-2.5",
          "hover:bg-accent-light hover:text-primary",
        )}
      >
        <Check aria-hidden="true" className={cn("size-4 shrink-0", !selected && "invisible")} />
        {text}
      </button>
    </li>
  );

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-size="sm"
          aria-label={t("timeRange.value", { range: fullLabel })}
          className={cn(SELECT_TRIGGER_CLASS, "w-full", className)}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Clock aria-hidden="true" className="text-muted size-4" />
            <span className="truncate">{label}</span>
          </span>
          <ChevronDownIcon aria-hidden="true" className="text-muted size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto max-w-[calc(100vw-2rem)] p-1">
        <ul aria-label={t("timeRange.label")} className="min-w-56">
          {PERIODS.map((days) =>
            option(
              // While the custom range is open, only "Aangepast" is ticked.
              !custom && value.kind === "period" && value.days === days,
              t(`period.last.${periodKey(days)}`),
              () => pickPeriod(days),
              String(days),
            ),
          )}
          {option(
            custom,
            t("timeRange.custom"),
            () => {
              setCustom(true);
              setProblem(null);
            },
            "custom",
          )}
        </ul>
        {custom && (
          <form
            aria-label={t("timeRange.custom")}
            className="mt-1 space-y-3 border-t p-2 pt-3"
            onSubmit={(event) => {
              event.preventDefault();
              apply();
            }}
          >
            <RangeEnd
              label={t("timeRange.from")}
              dayLabel={t("timeRange.startDay")}
              timeLabel={t("timeRange.startTime")}
              value={draft.from}
              onChange={(from) => {
                setDraft({ ...draft, from });
                setProblem(null);
              }}
            />
            <RangeEnd
              label={t("timeRange.to")}
              dayLabel={t("timeRange.endDay")}
              timeLabel={t("timeRange.endTime")}
              value={draft.to}
              onChange={(to) => {
                setDraft({ ...draft, to });
                setProblem(null);
              }}
            />
            {problem && (
              <p role="alert" className="text-destructive text-xs">
                {t(`timeRange.problem.${problem}`)}
              </p>
            )}
            <p className="text-muted text-xs">{t("timeRange.belgianTime")}</p>
            <div className="flex justify-end">
              <Button type="submit" size="sm">
                {t("timeRange.apply")}
              </Button>
            </div>
          </form>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface RangeEndProps {
  /** "Van", "Tot". */
  label: string;
  /** The day's and the time's names for screen readers: "Begindag", "Beginuur". */
  dayLabel: string;
  timeLabel: string;
  value: EndDraft;
  onChange: (value: EndDraft) => void;
}

/** One end of a custom range: its name, a day and a time. */
function RangeEnd({ label, dayLabel, timeLabel, value, onChange }: RangeEndProps) {
  const { t } = useTranslation();
  return (
    <fieldset className="space-y-1">
      <legend className="text-foreground text-xs font-semibold">{label}</legend>
      <div className="flex gap-2">
        <DayPicker
          value={value.day}
          onChange={(day) => onChange({ ...value, day })}
          label={dayLabel}
          placeholder={t("timeRange.chooseDay")}
          clearable={false}
          className="w-40"
        />
        <Input
          inputMode="numeric"
          autoComplete="off"
          aria-label={timeLabel}
          placeholder="08:00"
          value={value.time}
          onChange={(event) => onChange({ ...value, time: event.target.value })}
          // "8.30" reads "08:30" once the box is left; anything else stays, for the check to name.
          onBlur={() => onChange({ ...value, time: parseTime(value.time) ?? value.time })}
          className="h-9 w-20 tabular-nums pointer-coarse:h-10"
        />
      </div>
    </fieldset>
  );
}
